/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Row, Col } from "react-bootstrap";
import { Button, FieldGroup, Select } from "core/components";
import { useFormContext, Controller } from "react-hook-form";
import ChangeOrderContext from "./ProjectChangeOrderContext";
import { TaskFormData, ChangeOrderFormData } from "core/schema";
import { IMaskInput } from "react-imask";
import ChangeOrderTaskItemsContext from "./ChangeOrderTaskItemsContext";

type ChangeOrderTaskItemFormProps = {
  index: number;
  tasks: Array<TaskFormData>;
};

const ChangeOrderTaskItemForm = ({ parentIndex, index, remove, items, changeOrder, isItemsLoading }: ChangeOrderTaskItemFormProps): JSX.Element => {
  const { setItemsForDestruction } = React.useContext(ChangeOrderContext);
  const { selectedItems, setSelectedItems } = React.useContext(ChangeOrderTaskItemsContext);
  const fieldItem = `change_order_tasks_attributes[${parentIndex}].change_order_task_items_attributes[${index}]`;
  const { control, formState, setValue, resetField, watch } = useFormContext<ChangeOrderFormData>();
  const errors = formState.errors?.change_order_tasks_attributes?.[parentIndex]?.change_order_task_items_attributes?.[index];
  const templateTaskItem = watch(fieldItem);
  const budgeted_quantity = watch(`${fieldItem}.budgeted_quantity`);
  const budgeted_unit_rate = watch(`${fieldItem}.budgeted_unit_rate`);
  const [selectedItem, setSelectedItem] = React.useState(items.find((item) => item.id == templateTaskItem?.item_id));
  const origBudgetedQuantity =
    changeOrder.change_order_tasks_attributes?.[parentIndex]?.change_order_task_items_attributes?.[index]?.budgeted_quantity || 0;

  const calculateUnitPrice = (quantity: number, unit_rate: number): number => {
    if (unit_rate <= 0 || quantity <= 0) {
      return 0;
    } else {
      return quantity * unit_rate;
    }
  };

  const addToSelectedItems = (item) => {
    if (selectedItems.find((i) => i.id == item.id)) {
      return;
    } else {
      setSelectedItems((value) => [...value, item]);
    }
  };

  const removeFromSelectedItems = (item) => {
    if (selectedItems.find((selectedItem) => selectedItem.id == item?.id)) {
      setSelectedItems(selectedItems.filter((selectedItem) => selectedItem.id != item.id));
    }
  };

  React.useEffect(() => {
    if (selectedItem) {
      addToSelectedItems(selectedItem);
    }
  }, [selectedItem]);

  return (
    <Row className="align-items-center">
      <Col lg={3}>
        <Controller
          name={`${fieldItem}.item_id`}
          control={control}
          render={({ field }) => (
            <Select
              label="Item"
              placeholder="Select an Item"
              required
              isLoading={isItemsLoading}
              noOptionsMessage={() => "No available items for the selected task."}
              options={items.filter((item) => !selectedItems.find((selected) => selected.id == item.id && selectedItem?.id != item.id))}
              getOptionLabel={(option) => `${option.code} - ${option.name}`}
              getOptionValue={(option) => option.id}
              isOptionDisabled={(option) => !option.active}
              disabled={!!templateTaskItem.task_item_id}
              onChange={(option, actionMeta) => {
                field.onChange(option?.id);

                if (selectedItem) {
                  removeFromSelectedItems(selectedItem);
                }
                setSelectedItem(option);

                if (actionMeta.action == "select-option") {
                  setValue(`${fieldItem}.unit_measure_id`, option.unit_measure_id);
                  setValue(`${fieldItem}.budgeted_unit_rate`, option.unit_rate);
                  addToSelectedItems(option);
                } else {
                  setValue(`${fieldItem}.unit_measure_id`, null);
                  setValue(`${fieldItem}.budgeted_unit_rate`, "");
                  removeFromSelectedItems(option || actionMeta?.removedValues[0]);
                }
              }}
              ref={field.ref}
              value={items.find((item) => item.id == field.value) || null}
              errorMessage={errors?.item_id?.message}
            />
          )}
        />
      </Col>
      <Col>
        <Controller
          name={`${fieldItem}.budgeted_quantity`}
          control={control}
          render={({ field }) => (
            <FieldGroup label="Budgeted Quantity" required errorMessage={errors?.budgeted_quantity?.message}>
              <div className="d-flex">
                <IMaskInput
                  mask={Number}
                  padFractionalZeros={true}
                  thousandsSeparator=","
                  radix="."
                  value={field.value?.toString()}
                  unmask={true}
                  inputRef={field.ref}
                  onAccept={(value) => {
                    field.onChange(value);

                    if (templateTaskItem.task_item_id) {
                      if (+value < +origBudgetedQuantity) setValue(`${fieldItem}.deducted_quantity`, +origBudgetedQuantity - +value);
                      else setValue(`${fieldItem}.deducted_quantity`, 0);
                    }
                  }}
                  className="field"
                  placeholder="0.00"
                  min={0}
                  max={9999999}
                  disabled={templateTaskItem.task_item_id && !templateTaskItem.can_change_budgeted_quantity}
                />
                <span
                  className="field"
                  style={{
                    flex: 1,
                    alignItems: "center",
                    fontWeight: "bold",
                    backgroundColor: "#e9ecef",
                    border: "1px solid #ced4da",
                    whiteSpace: "nowrap",
                    paddingTop: "10px",
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0
                  }}
                >
                  {selectedItem?.unit_measure?.code || "UoM"}
                </span>
              </div>
            </FieldGroup>
          )}
        />
      </Col>
      <Col>
        <Controller
          name={`${fieldItem}.budgeted_unit_rate`}
          control={control}
          render={({ field }) => (
            <FieldGroup label="Budgeted Unit Rate">
              <div className="d-flex">
                <span
                  className="field"
                  style={{
                    flex: 1,
                    alignItems: "center",
                    fontWeight: "bold",
                    backgroundColor: "#e9ecef",
                    border: "1px solid #ced4da",
                    whiteSpace: "nowrap",
                    paddingTop: "10px",
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0
                  }}
                >
                  ₱
                </span>
                <IMaskInput
                  mask={Number}
                  padFractionalZeros={true}
                  thousandsSeparator=","
                  radix="."
                  value={field.value?.toString()}
                  unmask={true}
                  inputRef={field.ref}
                  onAccept={(value) => {
                    field.onChange(value);
                  }}
                  className="field"
                  placeholder="N/A"
                  min={0}
                  max={9999999}
                  readOnly={true}
                />
              </div>
            </FieldGroup>
          )}
        />
      </Col>
      <Col>
        <FieldGroup label="Budgeted Amount">
          <div className="d-flex">
            <span
              className="field"
              style={{
                flex: 1,
                alignItems: "center",
                fontWeight: "bold",
                backgroundColor: "#e9ecef",
                border: "1px solid #ced4da",
                whiteSpace: "nowrap",
                paddingTop: "10px",
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0
              }}
            >
              ₱
            </span>
            <IMaskInput
              mask={Number}
              padFractionalZeros={true}
              thousandsSeparator=","
              radix="."
              unmask={true}
              className="field"
              placeholder="N/A"
              readOnly={true}
              value={calculateUnitPrice(parseFloat(budgeted_quantity), parseFloat(budgeted_unit_rate))?.toString()}
            />
          </div>
        </FieldGroup>
      </Col>
      {changeOrder.type == "replace" && (
        <Col>
          <Controller
            name={`${fieldItem}.actual_quantity`}
            control={control}
            render={({ field }) => (
              <FieldGroup label="Actual Issued Quantity">
                <IMaskInput
                  mask={Number}
                  padFractionalZeros={true}
                  thousandsSeparator=","
                  radix="."
                  value={field.value?.toString()}
                  unmask={true}
                  inputRef={field.ref}
                  onAccept={(value) => {
                    field.onChange(value);
                  }}
                  className="field"
                  placeholder="0.00"
                  min={0}
                  max={9999999}
                  readOnly={true}
                />
              </FieldGroup>
            )}
          />
        </Col>
      )}
      {!templateTaskItem.task_item_id && (
        <Col sm={1}>
          <div className="d-flex justify-content-end align-content-end">
            <Button
              className="button--danger"
              icon="minus"
              onClick={() => {
                if (templateTaskItem?.task_item_id) {
                  setItemsForDestruction((value) => [...value, { ...templateTaskItem, _destroy: "1" }]);
                }
                resetField(fieldItem, { defaultValue: {} });
                remove(index);
                removeFromSelectedItems(selectedItem);
              }}
            />
          </div>
        </Col>
      )}
    </Row>
  );
};

export default ChangeOrderTaskItemForm;
